import React, { useEffect, useState } from 'react';
import { Table, Tag, Button, message, Tooltip, Card } from 'antd';
import { useAuth } from '../../../../hooks/useAuth';
import { supabase } from '../../../../config/supabaseClient';
import { deleteExpiredUser, inviteTeamMember, resendInvitation } from '../../../../Utils/SupabaseServices/UserDatabase';
import './MembersList.css';
import { HiOutlineClock } from 'react-icons/hi';
import { useMediaQuery } from '@mui/material';

interface TeamMember {
    email: string;
    name?: string;
    phoneNumber?: string;
    role: string;
    token: string;
    status: string;
    created_at: string;
    creator_id: string;
    expires_at: string;
}

interface MembersListProps {
    isEmailChanged: boolean;
    userData: any;
}

const MembersList: React.FC<MembersListProps> = ({  userData, isEmailChanged }) => {
    const [members, setMembers] = useState<TeamMember[]>([]);
    const [loading, setLoading] = useState(true);
    const {  session, isMember, memberId } = useAuth();
    const userId = userData?.user_id;
    const [resendingTo, setResendingTo] = useState<string | null>(null);
    const isMobile = useMediaQuery('(max-width: 1199px)');

    useEffect(() => {
        loadMembers();

        // Set up real-time subscription
        const creatorUserId = (isMember && session?.user?.email !== userData?.email) ? JSON.parse(localStorage.getItem("member") || '{}').user_id : userId;
        const subscription = supabase
            .channel('team_members_changes')
            .on(
                'postgres_changes',
                {
                    event: '*',
                    schema: 'public',
                    table: 'user',
                    filter: `user_id=eq.${creatorUserId}`
                },
                () => {
                    loadMembers();
                }
            )
            .subscribe();

        return () => {
            subscription.unsubscribe();
        };
    }, [userData, isEmailChanged, isMember]);

    const loadMembers = async () => {
        if (!userData) return;

        try {
            let creatorUserId;
            let currentMemberEmail = '';
            let oldMemberEmail = '';

            if (isMember && session?.user?.email !== userData?.email) {
                // When logged in as member, get creator's data and current member's email
                const memberData = JSON.parse(localStorage.getItem("member") || '{}');
                creatorUserId = memberData.user_id;
                currentMemberEmail = session?.user?.email || userData?.email || '';
                
                // Also get the original email from localStorage member data as fallback
                oldMemberEmail = memberData.email || '';
            } else {
                // When logged in as creator
                creatorUserId = userId;
            }

            const { data, error } = await supabase
                .from('user')
                .select('team_members, name, email, phoneNo, created_at')
                .eq('user_id', creatorUserId)
                .single();

            if (error) throw error;

            let membersToShow: TeamMember[] = [];

            if (isMember && session?.user?.email !== userData?.email) {
                // Show creator details
                const creatorMember: TeamMember = {
                    email: data.email || '',
                    name: data.name || '',
                    phoneNumber: data.phoneNo || '',
                    role: 'creator',
                    token: '',
                    status: 'active',
                    created_at: data.created_at || '',
                    creator_id: creatorUserId,
                    expires_at: ''
                };
                membersToShow.push(creatorMember);

                // Add other team members (excluding current logged-in member)
                // Use both current and old email for filtering during email transitions
                const otherMembers = (data?.team_members || []).filter((member: TeamMember) => {
                    const currentEmailMatch = member.email.toLowerCase() === currentMemberEmail.toLowerCase();
                    const oldEmailMatch = oldMemberEmail && member.email.toLowerCase() === oldMemberEmail.toLowerCase();
                    
                    // Exclude if email matches either current or old email
                    return !currentEmailMatch && !oldEmailMatch;
                });
                membersToShow = [...membersToShow, ...otherMembers];
            } else {
                // Show all team members for creator
                membersToShow = data?.team_members || [];
            }

            // Sort members by status (active first) and then by creation date
            const sortedMembers = membersToShow.sort((a: TeamMember, b: TeamMember) => {
                if (a.status === b.status) {
                    return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
                }
                return a.status === 'active' ? -1 : 1;
            });

            setMembers(sortedMembers);
        } catch (error) {
            console.error('Error loading members:', error);
            // message.error('Failed to load team members');
        } finally {
            setLoading(false);
        }
    };

    const handleResendInvitation = async (email: string) => {
        if (!userData) return;

        const creatorUserId = (isMember && session?.user?.email !== userData?.email) ? JSON.parse(localStorage.getItem("member") || '{}').user_id : userId;

        setResendingTo(email);
        try {
            const result: any = await resendInvitation(email, creatorUserId as string);
            if (result.error) {
                // message.error(result.error);
            } else {
                // message.success(result.message);
                // setError
                loadMembers();
            }
        } catch (error) {
            // message.error('Failed to resend invitation');
        } finally {
            setResendingTo(null);
        }
    };

    const handleDeleteMember = async (email: string, type: string) => {
        if (!userData) return;
        setResendingTo(email)

        const creatorUserId = (isMember && session?.user?.email !== userData?.email) ? JSON.parse(localStorage.getItem("member") || '{}').user_id : userId;
        const currentUserId = userData?.user_id;

        const result = await deleteExpiredUser(email, creatorUserId as string, currentUserId as string, type);
        if (result) {
            loadMembers();
        }
        setResendingTo(null);
    };

    const getTimeRemaining = (expiresAt: string) => {
        const now = new Date();
        const expiry = new Date(expiresAt);
        const diff = expiry.getTime() - now.getTime();
        const hours = Math.floor(diff / (1000 * 60 * 60));
        return hours > 0 ? `${hours} hours` : 'Expired';
    };

    const renderMemberCard = (member: TeamMember) => (
        <Card key={member.email} className="member-card">
            <div className="member-card-content">
                <div className="member-card-header">
                    <div className="member-info">
                        <div className="member-name">{member.name || '-'}</div>
                        <div className="member-email">{member.email || '-'}</div>

                    </div>
                    <div className="member-status">
                        <Tag color={member.status === 'active' ? 'green' : 'orange'}>
                            {member.status.toUpperCase()}
                        </Tag>
                        {member.status === 'pending' && (
                            <Tooltip title={`Expires in ${getTimeRemaining(member.expires_at)}`}>
                                <HiOutlineClock className="clock-icon" />
                            </Tooltip>
                        )}
                    </div>
                </div>
                <div className="member-card-header member-card-info">
                    <div className="member-joined">{member.phoneNumber || '-'}</div>
                    <div className="member-joined">
                        Joined {new Date(member.created_at).toLocaleDateString(undefined, {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric'
                        })}
                    </div>
                </div>

                {(isMember && session?.user?.email === userData?.email) && (
                    <div className="member-actions">
                        {true || member.status === 'pending' && new Date(member.expires_at) < new Date() && (
                            <Button
                                className="resend-button"
                                loading={resendingTo === member.email}
                                onClick={() => handleResendInvitation(member.email)}
                            >
                                Resend Invitation
                            </Button>
                        )}

                        <Button
                            className="resend-button"
                            loading={resendingTo === member.email}
                            onClick={() => handleDeleteMember(member.email, "member")}
                        >
                            Delete Member
                        </Button>

                    </div>
                )}
            </div>
        </Card>
    );

    const columns = [
        {
            title: 'Email',
            dataIndex: 'email',
            key: 'email',
            render: (email: string) => (
                <div className="email-cell">{email}</div>
            ),
        },
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
            render: (name: string) => name || '-',
        },
        {
            title: 'Phone Number',
            dataIndex: 'phoneNumber',
            key: 'phoneNumber',
            render: (phoneNumber: string) => phoneNumber || '-',
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: (status: string, record: TeamMember) => (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Tag color={status === 'active' ? 'green' : 'orange'}>
                        {status.toUpperCase()}
                    </Tag>
                    {status === 'pending' && (
                        <Tooltip title={`Expires in ${getTimeRemaining(record.expires_at)}`}>
                            <HiOutlineClock style={{ color: '#9CA3AF' }} />
                        </Tooltip>
                    )}
                </div>
            ),
        },
        {
            title: 'Joined',
            dataIndex: 'created_at',
            key: 'created_at',
            render: (date: string) => new Date(date).toLocaleDateString(undefined, {
                year: 'numeric',
                month: 'short',
                day: 'numeric'
            }),
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (record: TeamMember) => {
                // Only show actions if user is creator (not a member) and not for creator record
                if (isMember && session?.user?.email !== userData?.email) {
                    return null;
                }

                return (
                    <div className="d-flex gap-2 flex-wrap">
                        {true ||(record.status === 'pending' && new Date(record.expires_at) < new Date()) && (<Button
                            className="resend-button"
                            loading={resendingTo === record.email}
                            onClick={() => handleResendInvitation(record.email)}
                        >
                            Resend Invitation
                        </Button>)}

                        <Button
                            className="resend-button"
                            loading={resendingTo === record.email}
                            onClick={() => handleDeleteMember(record.email, "member")}
                        >
                            Delete Member
                        </Button>
                    </div>
                )
            },
        }
    ];

    return (
        <div className="members-list">
            {isMobile ? (
                <div className="members-card-container">
                    {
                        members.length > 0 ? members.map(renderMemberCard) : <div className="no-members-found">No members found</div>
                    }
                </div>
            ) : (
                <Table
                    loading={loading}
                    dataSource={members}
                    columns={columns}
                    scroll={{ x: 1000 }}
                    className='analytics-modern-table'
                    rowKey="email"
                    pagination={{
                        pageSize: 5,
                        hideOnSinglePage: true,
                        position: ['bottomCenter']
                    }}
                />
            )}
        </div>
    );
};

export default MembersList;
